import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, Medal, Zap, TrendingUp, Award, Star } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

const LOCATIONS = ['Carrollwood', 'Countryside', 'Lakeland', 'Sarasota', 'Wesley Chapel'];
const DEPARTMENTS = ['FOH', 'BOH'];

export default function LeaderboardPage() {
  const { api, user } = useAuth();
  const [board, setBoard] = useState([]);
  const [myStreak, setMyStreak] = useState(null);
  const [filterDept, setFilterDept] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterDept && filterDept !== 'all_departments') params.append('department', filterDept);
      if (filterLocation && filterLocation !== 'all_locations') params.append('location', filterLocation);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const [boardRes, streakRes] = await Promise.all([
        api('get', `/leaderboard${qs}`),
        api('get', '/streaks/my')
      ]);
      setBoard(boardRes.data);
      setMyStreak(streakRes.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load leaderboard:', err);
    }
  }, [api, filterDept, filterLocation]);

  useEffect(() => { loadData(); }, [loadData]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-amber-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-stone-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-700" />;
    return <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-stone-500">#{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200';
    if (rank === 2) return 'bg-gradient-to-r from-stone-50 to-slate-50 border-stone-300';
    if (rank === 3) return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
    return 'bg-white border-stone-200';
  };

  const myEntry = board.find(e => e.user_id === user?.id);
  const hasFilters = (filterDept && filterDept !== 'all_departments') || (filterLocation && filterLocation !== 'all_locations');

  return (
    <div className="pb-20 md:pb-6">
      <motion.div {...fadeUp} className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Leaderboard
        </h1>
        <p className="text-stone-500 mt-1 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Compete with your team, earn points, build streaks
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div {...fadeUp} transition={{ delay: 0.05 }}>
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4" data-testid="leaderboard-filters">
          <CardContent className="p-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-stone-500 font-medium mb-1 block">Department</label>
                <Select value={filterDept} onValueChange={setFilterDept}>
                  <SelectTrigger className="rounded-xl" data-testid="filter-department">
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_departments">All Departments</SelectItem>
                    {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-stone-500 font-medium mb-1 block">Location</label>
                <Select value={filterLocation} onValueChange={setFilterLocation}>
                  <SelectTrigger className="rounded-xl" data-testid="filter-location">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_locations">All Locations</SelectItem>
                    {LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {hasFilters && (
                <button onClick={() => { setFilterDept(''); setFilterLocation(''); }}
                  className="text-xs text-[#1B2A4A] hover:underline font-medium pb-2.5" data-testid="clear-filters">
                  Clear filters
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* My Streak Card */}
      <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
        <Card className="bg-gradient-to-r from-[#1B2A4A]/5 to-blue-50 rounded-2xl border border-[#1B2A4A]/20 shadow-sm mb-6" data-testid="streak-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#1B2A4A]/70 mb-1">Your Streak</p>
                <div className="flex items-center gap-1.5">
                  <Zap className="w-6 h-6 text-[#D9480F]" />
                  <span className="text-3xl font-bold text-stone-900">{myStreak?.current_streak || 0}</span>
                  <span className="text-sm text-stone-500">day{(myStreak?.current_streak || 0) !== 1 ? 's' : ''}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-4">
                  <div><p className="text-xs text-stone-500">Best Streak</p><p className="text-lg font-bold text-stone-900">{myStreak?.longest_streak || 0}</p></div>
                  <div><p className="text-xs text-stone-500">Total Days</p><p className="text-lg font-bold text-stone-900">{myStreak?.total_activity_days || 0}</p></div>
                  {myEntry && <div><p className="text-xs text-stone-500">My Rank</p><p className="text-lg font-bold text-[#1B2A4A]">#{myEntry.rank}</p></div>}
                </div>
              </div>
            </div>
            {myEntry && (
              <div className="mt-4 pt-3 border-t border-[#1B2A4A]/10 flex items-center justify-between">
                <span className="text-xs text-stone-500">Total Points</span>
                <span className="text-lg font-bold text-[#1B2A4A]">{myEntry.points} pts</span>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Points Legend */}
      <motion.div {...fadeUp} transition={{ delay: 0.15 }}>
        <Card className="rounded-2xl border border-stone-200 shadow-sm mb-6" data-testid="points-info">
          <CardContent className="p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">How Points Work</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
              {[
                { label: 'Module', pts: '+100', icon: Award },
                { label: 'Test Pass', pts: '+50', icon: Star },
                { label: 'Task Done', pts: '+25', icon: TrendingUp },
                { label: 'Avg Score', pts: 'x2', icon: Trophy },
                { label: 'Streak Day', pts: '+10', icon: Zap },
              ].map((item) => (
                <div key={item.label} className="p-2 rounded-xl bg-stone-50">
                  <item.icon className="w-4 h-4 text-[#D9480F] mx-auto mb-1" />
                  <p className="text-xs font-bold text-[#D9480F]">{item.pts}</p>
                  <p className="text-[10px] text-stone-500">{item.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Ranking List */}
      <div className="space-y-2" data-testid="leaderboard-list">
        {board.length === 0 && (
          <Card className="bg-white rounded-2xl border border-stone-200">
            <CardContent className="p-8 text-center">
              <Trophy className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500">{hasFilters ? 'No results for the selected filters' : 'No leaderboard data yet. Complete training to earn points!'}</p>
            </CardContent>
          </Card>
        )}
        {board.map((entry, i) => {
          const isMe = entry.user_id === user?.id;
          return (
            <motion.div key={entry.user_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}>
              <Card className={`rounded-2xl border shadow-sm transition-all ${getRankBg(entry.rank)} ${isMe ? 'ring-2 ring-[#1B2A4A]/30' : ''}`}
                data-testid={`leaderboard-entry-${entry.rank}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="shrink-0">{getRankIcon(entry.rank)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-stone-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {entry.name} {isMe && <span className="text-[#1B2A4A]">(You)</span>}
                        </h3>
                        <Badge className="bg-stone-100 text-stone-600 text-xs shrink-0">{entry.role}</Badge>
                        {entry.department && (
                          <Badge className={entry.department === 'FOH' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-orange-100 text-orange-700 text-xs'}>{entry.department}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                        <span>{entry.modules_completed} modules</span>
                        <span>{entry.tests_passed} tests</span>
                        <span>{entry.tasks_completed} tasks</span>
                        {entry.location && <span className="text-stone-400">{entry.location}</span>}
                        {entry.streak_days > 0 && (
                          <span className="flex items-center gap-0.5 text-[#D9480F]">
                            <Zap className="w-3 h-3" /> {entry.streak_days}d streak
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xl font-bold text-stone-900">{entry.points}</p>
                      <p className="text-[10px] text-stone-500">points</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
